import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GraduationCap, PlayCircle, CheckCircle2, Settings, BookOpen, Play, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { treinamentoCursoService } from '@/services/treinamentosApi';
import { TreinamentoCategoria, TreinamentoCurso } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export default function Treinamentos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const [categorias, setCategorias] = useState<TreinamentoCategoria[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await treinamentoCursoService.catalogo();
        setCategorias(data);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Erro ao carregar treinamentos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const todosCursos = useMemo(
    () => categorias.flatMap((c) => c.cursos ?? []),
    [categorias],
  );
  const totalCursos = todosCursos.length;

  // destaque: o curso mais avançado em andamento, senão o primeiro com capa, senão o primeiro
  const destaque = useMemo(() => {
    const emAndamento = todosCursos
      .filter((c) => (c.progresso?.percent ?? 0) > 0 && (c.progresso?.percent ?? 0) < 100)
      .sort((a, b) => (b.progresso?.percent ?? 0) - (a.progresso?.percent ?? 0))[0];
    return emAndamento ?? todosCursos.find((c) => c.capa_url) ?? todosCursos[0] ?? null;
  }, [todosCursos]);

  // "Continue assistindo": cursos iniciados e não concluídos
  const continueAssistindo = useMemo(
    () =>
      todosCursos
        .filter((c) => (c.progresso?.percent ?? 0) > 0 && (c.progresso?.percent ?? 0) < 100)
        .sort((a, b) => (b.progresso?.percent ?? 0) - (a.progresso?.percent ?? 0)),
    [todosCursos],
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-8">
          <Skeleton className="aspect-[21/9] w-full rounded-2xl" />
          <div className="flex gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video w-72 shrink-0 rounded-xl" />
            ))}
          </div>
        </div>

        <GuiaModuloFlutuante imagemSrc="/guias-modulos/treinamentos.png" titulo="Treinamentos" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8 pb-10">
        {/* Cabeçalho */}
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <GraduationCap className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Treinamentos</h1>
              <p className="text-sm text-muted-foreground">Assista, aprenda e acompanhe seu progresso</p>
            </div>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/treinamentos/gerenciar')}>
              <Settings className="mr-2 h-4 w-4" /> Gerenciar
            </Button>
          )}
        </div>

        {totalCursos === 0 ? (
          <div>
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed py-20 text-center">
              <BookOpen className="h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground">Nenhum curso disponível ainda.</p>
              {isAdmin && (
                <Button onClick={() => navigate('/treinamentos/gerenciar')}>
                  <Settings className="mr-2 h-4 w-4" /> Criar primeiro curso
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {destaque && <Hero curso={destaque} onPlay={() => navigate(`/treinamentos/curso/${destaque.id}`)} />}

            {continueAssistindo.length > 0 && (
              <Row title="Continue assistindo" cursos={continueAssistindo} onOpen={(id) => navigate(`/treinamentos/curso/${id}`)} />
            )}

            {categorias
              .filter((cat) => (cat.cursos?.length ?? 0) > 0)
              .map((cat) => (
                <Row
                  key={cat.id}
                  title={cat.nome}
                  global={cat.empresa_id === null}
                  cursos={cat.cursos!}
                  onOpen={(id) => navigate(`/treinamentos/curso/${id}`)}
                />
              ))}
          </>
        )}
      </div>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/treinamentos.png" titulo="Treinamentos" />
    </AppLayout>
  );
}

// ─── Hero em destaque ───────────────────────────────────────────────────────────

function Hero({ curso, onPlay }: { curso: TreinamentoCurso; onPlay: () => void }) {
  const pct = curso.progresso?.percent ?? 0;
  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="aspect-[21/9] max-h-[420px] w-full">
        {curso.capa_url ? (
          <img src={curso.capa_url} alt={curso.titulo} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/70 to-primary">
            <GraduationCap className="h-24 w-24 text-primary-foreground/40" />
          </div>
        )}
      </div>
      {/* degradês para legibilidade */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

      <div className="absolute inset-0 flex flex-col justify-end gap-3 p-5 md:max-w-2xl md:p-8">
        {pct === 100 ? (
          <Badge className="w-fit bg-green-600 hover:bg-green-600">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Concluído
          </Badge>
        ) : pct > 0 ? (
          <Badge variant="secondary" className="w-fit">Em andamento • {pct}%</Badge>
        ) : (
          <Badge variant="secondary" className="w-fit">Em destaque</Badge>
        )}
        <h2 className="text-2xl font-bold text-white drop-shadow md:text-4xl">{curso.titulo}</h2>
        {curso.descricao && (
          <p className="line-clamp-2 max-w-xl text-sm text-white/80 md:text-base">{curso.descricao}</p>
        )}
        <div className="mt-1 flex items-center gap-3">
          <Button size="lg" onClick={onPlay} className="gap-2">
            <Play className="h-5 w-5 fill-current" />
            {pct > 0 && pct < 100 ? 'Continuar' : pct === 100 ? 'Rever' : 'Assistir'}
          </Button>
          {pct > 0 && (
            <span className="text-sm text-white/70">
              {curso.progresso?.concluidas}/{curso.progresso?.total} aulas
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Fileira horizontal (carrossel) ──────────────────────────────────────────────

function Row({
  title,
  cursos,
  onOpen,
  global,
}: {
  title: string;
  cursos: TreinamentoCurso[];
  onOpen: (id: string) => void;
  global?: boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <section className="group/row space-y-3">
      <div className="rounded-lg border bg-card p-4 flex items-center gap-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        {global && <Badge variant="secondary" className="text-[10px]">Global</Badge>}
      </div>

      <div className="relative">
        {/* botões de scroll (desktop) */}
        <button
          onClick={() => scroll(-1)}
          className="absolute left-1 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full bg-background/80 p-2 shadow-md backdrop-blur transition-opacity group-hover/row:flex md:flex"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div
          ref={scroller}
          className="flex gap-3 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {cursos.map((curso) => (
            <PosterCard key={curso.id} curso={curso} onClick={() => onOpen(curso.id)} />
          ))}
        </div>
        <button
          onClick={() => scroll(1)}
          className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full bg-background/80 p-2 shadow-md backdrop-blur transition-opacity group-hover/row:flex md:flex"
          aria-label="Próximo"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}

// ─── Card poster (thumb 16:9) ─────────────────────────────────────────────────────

function PosterCard({ curso, onClick }: { curso: TreinamentoCurso; onClick: () => void }) {
  const pct = curso.progresso?.percent ?? 0;
  const concluido = pct === 100;
  const iniciado = pct > 0;

  return (
    <button
      onClick={onClick}
      className="group/card relative w-60 shrink-0 overflow-hidden rounded-xl bg-card text-left shadow-sm ring-1 ring-border transition-all duration-200 hover:z-10 hover:scale-[1.04] hover:shadow-xl sm:w-64 md:w-72"
    >
      {/* thumbnail 16:9 */}
      <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-primary/70 to-primary">
        {curso.capa_url ? (
          <img
            src={curso.capa_url}
            alt={curso.titulo}
            className="h-full w-full object-cover transition-transform duration-300 group-hover/card:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <GraduationCap className="h-12 w-12 text-primary-foreground/70" />
          </div>
        )}

        {/* overlay play no hover */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover/card:bg-black/35">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 opacity-0 transition-opacity group-hover/card:opacity-100">
            <Play className="h-6 w-6 fill-primary text-primary" />
          </div>
        </div>

        {/* badges */}
        {curso.status === 'rascunho' && (
          <Badge variant="secondary" className="absolute right-2 top-2 text-[10px]">Rascunho</Badge>
        )}
        {concluido && (
          <Badge className="absolute left-2 top-2 bg-green-600 text-[10px] hover:bg-green-600">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Concluído
          </Badge>
        )}

        {/* barra de progresso na base da thumb */}
        {iniciado && !concluido && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/40">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>

      {/* legenda */}
      <div className="space-y-1 p-3">
        <h4 className="line-clamp-1 text-sm font-semibold">{curso.titulo}</h4>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{curso.progresso?.total ?? 0} aulas</span>
          <span className="flex items-center gap-1 font-medium text-primary">
            <PlayCircle className="h-3.5 w-3.5" />
            {concluido ? 'Rever' : iniciado ? `Continuar · ${pct}%` : 'Começar'}
          </span>
        </div>
      </div>
    </button>
  );
}
